
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Bug, Wrench, Target, Navigation } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface EnhancedMatchingControlsProps {
  userReportsCount: number;
  radiusLoading: boolean;
  hasNullData: boolean;
  onUpdateCoordinates: () => Promise<void>;
  onDebug: () => Promise<void>;
  onFixNullData: () => Promise<void>;
  onEnhancedRefresh: () => Promise<void>;
}

export const EnhancedMatchingControls = ({
  userReportsCount,
  radiusLoading,
  hasNullData,
  onUpdateCoordinates,
  onDebug,
  onFixNullData,
  onEnhancedRefresh
}: EnhancedMatchingControlsProps) => {
  const [refreshing, setRefreshing] = useState(false);
  const [fixing, setFixing] = useState(false);

  const handleEnhancedRefresh = async () => {
    setRefreshing(true);
    try {
      await onEnhancedRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const handleFixNullData = async () => {
    setFixing(true);
    try {
      await onFixNullData();
    } finally {
      setFixing(false);
    }
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <h3 className="text-lg font-semibold text-green-800 flex items-center space-x-2">
          <Target size={20} />
          <span>Your Regional Reports</span>
          <Badge variant="secondary">{userReportsCount}</Badge>
          <Badge className="bg-purple-100 text-purple-800 border-purple-200 flex items-center space-x-1">
            <Navigation size={12} />
            <span>Enhanced Radius Matching</span>
          </Badge>
        </h3>
      </div>
      <div className="flex items-center space-x-2">
        <Button 
          onClick={onUpdateCoordinates} 
          variant="outline" 
          size="sm"
          disabled={radiusLoading}
          className="text-blue-600 border-blue-300 hover:bg-blue-50"
        >
          <MapPin size={14} className={`mr-2 ${radiusLoading ? 'animate-pulse' : ''}`} />
          Update Coords
        </Button>
        <Button 
          onClick={onDebug} 
          variant="outline" 
          size="sm"
          className="text-gray-600 border-gray-300 hover:bg-gray-50"
        >
          <Bug size={14} className="mr-2" />
          Debug
        </Button>
        {hasNullData && (
          <Button 
            onClick={handleFixNullData} 
            variant="outline" 
            size="sm"
            disabled={fixing}
            className="text-orange-600 border-orange-300 hover:bg-orange-50"
          >
            <Wrench size={14} className={`mr-2 ${fixing ? 'animate-spin' : ''}`} />
            {fixing ? 'Fixing...' : 'Fix NULL Data'}
          </Button>
        )}
        <Button 
          onClick={handleEnhancedRefresh} 
          variant="outline" 
          size="sm"
          disabled={refreshing || radiusLoading}
        >
          <Target size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Enhanced Matching...' : 'Enhanced Re-match'}
        </Button>
      </div>
    </div>
  );
};
